import React, { useState } from 'react';
import { useAuth } from '../../context/AuthProvider';
import { supabase } from '../../lib/supabaseClient';
import { Mail, Lock, User, Eye, EyeOff, ArrowLeft, Sparkles, Popcorn, Clock } from 'lucide-react';
import { Logo } from '../ui/Logo';
import { emailConfirmedUrl, resetPasswordUrl } from '../../lib/authRedirects';

const TMDB_SM = 'https://image.tmdb.org/t/p/w342';

/** Same collection collage as HomePage — slightly tighter for the auth brand panel. */
const collectionWall = [
  { path: '/iiZZdoQBEYBv6id8su7ImL0oCbD.jpg', r: -4, y: 0 },   // Spider-Verse
  { path: '/d5iIlFn5s0ImszYzBPb8JPIfbXD.jpg', r: 3, y: 14 },   // Pulp Fiction
  { path: '/fNOH9f1aA7XRTzl1sAOx9iF553Q.jpg', r: -2, y: 4 },   // Back to the Future
  { path: '/oYuLEt3zVCKq57qu2F8dT7NIa6f.jpg', r: 5, y: 18 },   // Inception
  { path: '/wuMc08IPKEatf9rnMNXvIDxqP4W.jpg', r: -5, y: 2 },   // Harry Potter
  { path: '/3Ya1RurP2NqaYoiok4DmfO41rCC.jpg', r: 2, y: 10 },   // Friday
  { path: '/ty8TGRuvJLPUmAR1H1nRIsgwvim.jpg', r: -3, y: 20 },  // Gladiator
  { path: '/6oom5QYQ2yQTMJIbnvbkBL9cHo6.jpg', r: 4, y: 6 },    // The Lord of the Rings
];

export function AuthPage() {
  const { signIn } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const [signupSent, setSignupSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleResendConfirmation = async () => {
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: emailConfirmedUrl()
        }
      });

      if (error) {
        setError(error.message);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isForgotPassword) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: resetPasswordUrl()
        });

        if (error) {
          setError(error.message);
        } else {
          setResetSent(true);
        }
      } else if (isLogin) {
        await signIn(email, password);
      } else {
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }

        // Sign up with name in metadata
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: emailConfirmedUrl(),
            data: {
              full_name: name,
              first_name: name.split(' ')[0]
            }
          }
        });

        if (error) {
          setError(error.message);
        } else {
          setSignupSent(true);
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain flex min-h-screen">
      {/* Brand panel (desktop) */}
      <div className="relative hidden w-[46%] flex-col overflow-hidden border-r border-white/[0.06] p-12 lg:flex">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(700px 420px at 20% 0%, hsl(358 70% 34% / 0.28), transparent 70%), radial-gradient(600px 400px at 90% 100%, hsl(39 90% 45% / 0.10), transparent 65%)'
          }}
        />

        {/* Wordmark */}
        <div className="relative z-10">
          <Logo size="lg" linked={false} className="text-5xl drop-shadow-[0_0_28px_hsl(358_80%_50%_/_0.28)]" />
        </div>

        {/* Collection collage — under the wordmark, left-aligned; clear in the center, fading radially to each corner.
            Box is tall enough to hold the whole stack so the radial mask (not a hard clip) does all the fading. */}
        <div
          className="pointer-events-none relative z-0 mt-12 h-[380px] w-full overflow-hidden"
          aria-hidden="true"
          style={{
            WebkitMaskImage:
              'radial-gradient(ellipse closest-side at 46% 42%, black 0%, black 34%, rgba(0,0,0,0.5) 66%, transparent 100%)',
            maskImage:
              'radial-gradient(ellipse closest-side at 46% 42%, black 0%, black 34%, rgba(0,0,0,0.5) 66%, transparent 100%)',
          }}
        >
          <div className="absolute -left-3 top-0 rotate-[-3deg]">
            <div className="flex -space-x-5">
              {collectionWall.slice(0, 4).map((p, i) => (
                <img
                  key={`r1-${i}`}
                  src={`${TMDB_SM}${p.path}`}
                  alt=""
                  className="poster-frame w-32 brightness-[0.68] saturate-[0.8]"
                  style={{ transform: `rotate(${p.r}deg) translateY(${p.y}px)`, zIndex: i % 2 === 0 ? 2 : 1 }}
                />
              ))}
            </div>
            <div className="-mt-3 flex translate-x-9 -space-x-5">
              {collectionWall.slice(4).map((p, i) => (
                <img
                  key={`r2-${i}`}
                  src={`${TMDB_SM}${p.path}`}
                  alt=""
                  className="poster-frame w-32 brightness-[0.68] saturate-[0.8]"
                  style={{ transform: `rotate(${p.r}deg) translateY(${p.y}px)`, zIndex: i % 2 === 0 ? 2 : 1 }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Messaging — pinned toward the bottom */}
        <div className="relative z-10 mt-auto space-y-10">
          <div>
            <h2 className="display mb-3 max-w-md text-4xl font-semibold leading-[1.1]">
              Stop browsing. <span className="text-gradient-dusk">Start watching.</span>
            </h2>
            <p className="max-w-sm leading-relaxed text-muted-foreground">
              Tell us three movies you love. Get personalized, high-quality recommendations in seconds.
            </p>
          </div>
          <ul className="space-y-3 text-sm text-muted-foreground">
            <li className="flex items-center gap-3">
              <Sparkles className="h-4 w-4 shrink-0 text-primary" />
              AI recommendations tuned to your taste
            </li>
            <li className="flex items-center gap-3">
              <Popcorn className="h-4 w-4 shrink-0 text-primary" />
              One watchlist across every streaming service
            </li>
            <li className="flex items-center gap-3">
              <Clock className="h-4 w-4 shrink-0 text-primary" />
              From “what should we watch?” to pressing play in minutes
            </li>
          </ul>
        </div>

        <p className="relative z-10 mt-10 text-xs tracking-wide text-muted-foreground/60">
          © {new Date().getFullYear()} MovieMe
        </p>
      </div>

      {/* Form panel */}
      <div className="flex flex-1 items-center justify-center p-4 py-12">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center lg:hidden">
            <div className="mb-8 flex justify-center">
              <Logo size="lg" linked={false} />
            </div>
          </div>

          <div className="animate-rise text-center">
            <h1 className="display mb-2 text-3xl font-semibold">
              {isForgotPassword
                ? 'Reset Password'
                : isLogin
                ? 'Welcome Back'
                : 'Create Account'}
            </h1>
            <p className="text-muted-foreground">
              {isForgotPassword
                ? 'Enter your email to receive a password reset link'
                : isLogin
                ? 'Sign in to access your movie recommendations'
                : 'Join MovieMe to discover your next favorite film'
              }
            </p>
          </div>

          <div className="panel animate-rise delay-75 space-y-6 p-8">
            {error && (
              <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-red-300">
                {error}
              </div>
            )}

            {resetSent ? (
              <div className="space-y-4 text-center">
                <div className="rounded-xl border border-emerald-400/25 bg-emerald-400/10 px-4 py-3 text-left text-emerald-300">
                  <p className="font-medium text-foreground">Check your email</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    We sent a password reset link to {email}. It opens securely on moviemeapp.com in
                    your browser. After you save the new password, return here and sign in.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsForgotPassword(false);
                    setResetSent(false);
                    setError(null);
                  }}
                  className="inline-flex min-h-[44px] items-center gap-2 font-medium text-primary transition-colors hover:text-gold-soft"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Sign In
                </button>
              </div>
            ) : signupSent ? (
              <div className="space-y-4 text-center">
                <div className="rounded-xl border border-accent/25 bg-accent/10 px-4 py-3 text-left text-foreground">
                  <p className="mb-2 font-medium">Check your email to confirm your account</p>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    We sent a confirmation link to {email}. It opens securely on moviemeapp.com in
                    your browser. After confirmation, return here and sign in.
                  </p>
                  <p className="mt-2 text-xs text-accent">
                    You cannot sign in until you confirm your email address.
                  </p>
                </div>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleResendConfirmation}
                    disabled={loading}
                    className="btn-marquee w-full px-4 py-3"
                  >
                    {loading ? 'Sending...' : 'Resend confirmation email'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSignupSent(false);
                      setIsLogin(true);
                      setError(null);
                      setEmail('');
                      setName('');
                      setPassword('');
                      setConfirmPassword('');
                    }}
                    className="inline-flex items-center gap-2 font-medium text-primary transition-colors hover:text-gold-soft"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Sign In
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && !isForgotPassword && (
                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground">
                      Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3.5 top-3.5 h-5 w-5 text-muted-foreground/60" />
                      <input
                        id="name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="input-dark pl-11"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-3.5 h-5 w-5 text-muted-foreground/60" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input-dark pl-11"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>

                {!isForgotPassword && (
                  <div>
                    <div className="mb-1.5 flex items-center justify-between">
                      <label htmlFor="password" className="block text-sm font-medium text-foreground">
                        Password
                      </label>
                      {isLogin && (
                        <button
                          type="button"
                          onClick={() => setIsForgotPassword(true)}
                          className="text-sm text-primary transition-colors hover:text-gold-soft"
                        >
                          Forgot password?
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-3.5 h-5 w-5 text-muted-foreground/60" />
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="input-dark pl-11 pr-12"
                        placeholder="Enter your password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-3.5 text-muted-foreground/60 transition-colors hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                )}

                {!isLogin && (
                  <div>
                    <label htmlFor="confirmPassword" className="mb-1.5 block text-sm font-medium text-foreground">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-3.5 h-5 w-5 text-muted-foreground/60" />
                      <input
                        id="confirmPassword"
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="input-dark pl-11"
                        placeholder="Confirm your password"
                        required
                      />
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-marquee w-full px-4 py-3.5 text-base"
                >
                  {loading
                    ? 'Loading...'
                    : isForgotPassword
                    ? 'Send Reset Link'
                    : isLogin
                    ? 'Sign In'
                    : 'Create Account'
                  }
                </button>
              </form>
            )}

            {!resetSent && !signupSent && (
              <div className="space-y-2 border-t border-white/[0.06] pt-5 text-center">
                {isForgotPassword ? (
                  <button
                    type="button"
                    onClick={() => {
                      setIsForgotPassword(false);
                      setError(null);
                    }}
                    className="inline-flex items-center gap-2 font-medium text-primary transition-colors hover:text-gold-soft"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Sign In
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setIsLogin(!isLogin);
                      setError(null);
                      setName('');
                    }}
                    className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {isLogin
                      ? <>Don't have an account? <span className="font-medium text-primary">Sign up</span></>
                      : <>Already have an account? <span className="font-medium text-primary">Sign in</span></>
                    }
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
